import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Link, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { capitalize, isEmpty } from "lodash-es";
import React, { ReactNode, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import {
    ANNUAL_REVENUE_DISPLAY_NAMES,
    AnnualRevenue,
    BUSINESS_VERTICAL_DISPLAY_NAMES,
    BusinessVertical,
    CASH_FLOW_DISPLAY_NAMES,
    CashFlow,
    NUMBER_OF_EMPLOYEES_DISPLAY_NAMES,
    NumberOfEmployees,
} from "../../types/unit";
import { US_STATE_OPTIONS } from "../../utils/addressUtils";
import { preventFormSubmitOnEnter } from "../../utils/formUtils";
import { onlyContainsNumbers, yupValidatePostalCode, yupValidateState } from "../../utils/validationUtils";
import { noExclusivelyNumbersAllowed } from "../../utils/validators";
import { ControllerCountriesSelect } from "../form/ControllerCountriesSelect";
import { ControllerDatePicker } from "../form/ControllerDatePicker";
import { ControllerRadioGroup } from "../form/ControllerRadioGroup";
import { ControllerStringOptionSelect } from "../form/ControllerStringOptionSelect";
import { FormCheckbox } from "../form/FormCheckbox";
import { FormInputWrapper } from "../form/FormInputWrapper";
import { FormPhoneInput, isPhoneNumberPossible } from "../form/FormPhoneInput";
import { FormSpreadAddressInput, IAddressFieldPaths } from "../form/FormSpreadAddressInput";
import { FormStringOptionSelect } from "../form/FormStringOptionSelect";
import { FormTextInput } from "../form/FormTextInput";
import { IStringOption } from "../input/StringOptionSelect";
import { IBusinessInformationFormData, UsOperations } from "./FormData";

const ENTITY_OPTIONS: IStringOption[] = [{ value: "Corporation" }, { value: "LLC" }, { value: "Partnership" }];

const EIN_REGEX = /^((\d{2}-\d{7})|(\d{9}))$/;
// https://stackoverflow.com/questions/61634973/yup-validation-of-website-using-url-very-strict
const WEBSITE_REGEX =
    /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;

const ADDRESS_FIELDS: IAddressFieldPaths = {
    street: "addressStreet",
    street2: "addressStreet2",
    city: "addressCity",
    state: "addressDistrict",
    zipCode: "addressZipcode",
    country: "addressCountry",
};

export const formSchema = yup.object({
    entityType: yup.string().required().label("Entity Type"),
    name: yup.string().required().test(noExclusivelyNumbersAllowed).label("Business Name"),
    stateOfIncorporation: yup.string().required().label("Incorporation State"),
    dba: yup.string().optional().test(noExclusivelyNumbersAllowed),
    ein: yup
        .string()
        .when("noEin", {
            is: false,
            then: (schema) => schema.required().matches(EIN_REGEX, "EIN is invalid"),
        })
        .label("EIN"),
    businessVertical: yup
        .mixed()
        .oneOf(Object.keys(BUSINESS_VERTICAL_DISPLAY_NAMES))
        .required()
        .label("Business Vertical"),
    yearOfIncorporation: yup
        .date()
        .nullable()
        .max(dayjs().year() + 1)
        .required()
        .label("Year of Incorporation"),
    annualRevenue: yup.mixed().oneOf(Object.keys(ANNUAL_REVENUE_DISPLAY_NAMES)).required().label("Annual Revenue"),
    numberOfEmployees: yup
        .mixed()
        .oneOf(Object.keys(NUMBER_OF_EMPLOYEES_DISPLAY_NAMES))
        .required()
        .label("Number of Employees"),
    cashFlow: yup.mixed().oneOf(Object.keys(CASH_FLOW_DISPLAY_NAMES)).required().label("Cash Flow"),
    usOperations: yup
        .mixed()
        .oneOf(Object.values(UsOperations))
        .required()
        .test(
            "us-operations-yes",
            "Operations in the United States are required. We are unable to support you at this time.",
            (usOperations) => usOperations === UsOperations.Yes,
        ),
    countriesOfOperation: yup
        .array()
        .label("Countries of Operation")
        .test(
            "contains-US",
            "Operations must include the United States",
            (countriesOfOperation) => countriesOfOperation?.includes("US") ?? false,
        ),
    phoneNumberFull: yup
        .string()
        .test({
            message: "Phone Number is required",
            test: (_value, ctx) => {
                return !isEmpty(ctx.parent.phoneNumber);
            },
        })
        .test({
            message: "Phone Number is invalid",
            test: (value, _ctx) => {
                return isPhoneNumberPossible(value ?? "");
            },
        })
        .label("Phone Number"),
    phoneCountryCode: yup.string().required(),
    phoneNumber: yup.string().required(),
    website: yup.string().required().matches(WEBSITE_REGEX, "Invalid website"),
    addressStreet: yup
        .string()
        .required()
        .test({
            message: "Street address must be valid",
            test: (value, _ctx) => !onlyContainsNumbers(value ?? ""),
        })
        .label("Street"),
    addressStreet2: yup.string().optional(),
    addressCity: yup
        .string()
        .required()
        .test({
            message: "City must be valid",
            test: (value, _ctx) => !onlyContainsNumbers(value ?? ""),
        })
        .label("City"),
    addressDistrict: yupValidateState("addressCountry").label("State / Province"),
    addressZipcode: yupValidatePostalCode("addressCountry").label("ZIP Code"),
    addressCountry: yup.string().required().matches(/^US$/, "Country must be US").label("Country"),
});

interface IProps {
    defaultValues?: IBusinessInformationFormData;
    onSubmit: SubmitHandler<IBusinessInformationFormData>;
    prevPageButton?: ReactNode;
    nextPageButton?: ReactNode;
}

export const BusinessInformationForm: React.FC<IProps> = ({
    defaultValues,
    onSubmit,
    prevPageButton,
    nextPageButton,
}) => {
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const resolver = yupResolver(formSchema);
    const methods = useForm<IBusinessInformationFormData>({
        mode: "onTouched",
        resolver,
        defaultValues: { addressCountry: "US", countriesOfOperation: ["US"], ...defaultValues },
    });
    const { handleSubmit, resetField, watch } = methods;

    const noEin = watch("noEin");
    useEffect(() => {
        if (noEin) {
            resetField("ein", { defaultValue: "" });
        }
    }, [noEin, resetField]);

    const usOperations = watch("usOperations");

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventFormSubmitOnEnter}>
                <Stack spacing={4}>
                    <Typography pb={1}>
                        First, we will need to collect some basic information about your business:
                    </Typography>
                    <FormStringOptionSelect
                        label="Entity Type"
                        name="entityType"
                        options={ENTITY_OPTIONS}
                        placeholder="Select an entity type"
                        required={true}
                    />
                    <FormStringOptionSelect
                        label="Incorporation State"
                        name="stateOfIncorporation"
                        options={US_STATE_OPTIONS}
                        placeholder="Select a state"
                        required={true}
                    />
                    <FormTextInput label="Legal Business Name" name="name" required={true} />
                    <FormTextInput
                        label="DBA Name"
                        name="dba"
                        helperText="Optional, also referred to as your business’s assumed, trade or fictitious name"
                    />
                    <Stack direction="column" spacing={1}>
                        <Box>
                            <FormTextInput label="EIN" name="ein" required={!noEin} disabled={noEin} />
                        </Box>
                        <Box>
                            <FormCheckbox label="I do not have an EIN" name="noEin" />
                        </Box>
                    </Stack>
                    <FormInputWrapper<IBusinessInformationFormData>
                        name="businessVertical"
                        required={true}
                        label="Business Vertical or Industry"
                    >
                        <ControllerStringOptionSelect<BusinessVertical, IBusinessInformationFormData>
                            name="businessVertical"
                            required={true}
                            options={BUSINESS_VERTICAL_OPTIONS}
                            placeholder="Select the business vertical"
                        />
                    </FormInputWrapper>
                    <FormInputWrapper<IBusinessInformationFormData>
                        name="yearOfIncorporation"
                        required={true}
                        label="Year of Incorporation"
                        helperText="Year of Incorporation of your US company"
                    >
                        <ControllerDatePicker<IBusinessInformationFormData>
                            name="yearOfIncorporation"
                            disableFuture={true}
                            views={["year"]}
                        />
                    </FormInputWrapper>
                    <FormInputWrapper<IBusinessInformationFormData>
                        name="annualRevenue"
                        required={true}
                        label="Annual Revenue in USD"
                    >
                        <ControllerStringOptionSelect<AnnualRevenue, IBusinessInformationFormData>
                            name="annualRevenue"
                            required={true}
                            options={ANNUAL_REVENUE_OPTIONS}
                            placeholder="Select annual revenue"
                        />
                    </FormInputWrapper>
                    <FormInputWrapper<IBusinessInformationFormData>
                        name="numberOfEmployees"
                        required={true}
                        label="Number of Employees"
                    >
                        <ControllerStringOptionSelect<NumberOfEmployees, IBusinessInformationFormData>
                            name="numberOfEmployees"
                            required={true}
                            options={NUMBER_OF_EMPLOYEES_OPTIONS}
                            placeholder="Select the number of employees"
                        />
                    </FormInputWrapper>
                    <FormInputWrapper<IBusinessInformationFormData>
                        name="cashFlow"
                        required={true}
                        label="Cash Flow Predictability"
                    >
                        <ControllerStringOptionSelect<CashFlow, IBusinessInformationFormData>
                            name="cashFlow"
                            required={true}
                            options={CASH_FLOW_OPTIONS}
                            placeholder="Select cash flow"
                        />
                    </FormInputWrapper>
                    <FormInputWrapper<IBusinessInformationFormData>
                        name="usOperations"
                        label="Do you have business operations in the United States?"
                        required={true}
                    >
                        <ControllerRadioGroup<UsOperations, IBusinessInformationFormData>
                            name="usOperations"
                            options={US_OPERATIONS_OPTIONS}
                            row={true}
                        />
                    </FormInputWrapper>
                    {usOperations === UsOperations.Yes && (
                        <FormInputWrapper<IBusinessInformationFormData>
                            name="countriesOfOperation"
                            required={true}
                            label="Countries of Operation"
                            helperText="Please select your primary countries of operations in which you have employees, vendors, customers, and/or investors."
                        >
                            <ControllerCountriesSelect<IBusinessInformationFormData> name="countriesOfOperation" />
                        </FormInputWrapper>
                    )}
                    <FormPhoneInput
                        label="Phone Number"
                        fullPhoneFieldName="phoneNumberFull"
                        countryCodeFieldName="phoneCountryCode"
                        localNumberFieldName="phoneNumber"
                        required={true}
                    />
                    <FormSpreadAddressInput
                        label="US Business Address"
                        description="Must be a US address, PO boxes are allowed"
                        addressFields={ADDRESS_FIELDS}
                        disableCountry={true}
                    />
                    <FormTextInput label="Business Website" name="website" sx={{ mt: 1, mb: 2 }} required={true} />
                    <Typography textAlign="justify">
                        By filling out this application, you understand and agree that Levro&apos;s use of your data is
                        governed by its{" "}
                        <Link href="https://www.levro.com/privacy" target="_blank">
                            Privacy Policy
                        </Link>
                        . Your data will be automatically saved until you complete and submit this application.
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" pt={1}>
                        <div>{prevPageButton}</div>
                        <div>{nextPageButton}</div>
                    </Stack>
                </Stack>
            </form>
        </FormProvider>
    );
};

const US_OPERATIONS_OPTIONS: Array<{ label: string; value: UsOperations }> = Object.values(UsOperations).map((t) => ({
    label: capitalize(t),
    value: t,
}));

const BUSINESS_VERTICAL_OPTIONS: Array<IStringOption<BusinessVertical>> = Object.entries(
    BUSINESS_VERTICAL_DISPLAY_NAMES,
).map(([key, displayName]) => ({ value: key as BusinessVertical, label: displayName }));

const ANNUAL_REVENUE_OPTIONS: Array<IStringOption<AnnualRevenue>> = Object.entries(ANNUAL_REVENUE_DISPLAY_NAMES).map(
    ([key, displayName]) => ({ value: key as AnnualRevenue, label: displayName }),
);

const CASH_FLOW_OPTIONS: Array<IStringOption<CashFlow>> = Object.entries(CASH_FLOW_DISPLAY_NAMES).map(
    ([key, displayName]) => ({ value: key as CashFlow, label: displayName }),
);

const NUMBER_OF_EMPLOYEES_OPTIONS: Array<IStringOption<NumberOfEmployees>> = Object.entries(
    NUMBER_OF_EMPLOYEES_DISPLAY_NAMES,
).map(([key, displayName]) => ({ value: key as NumberOfEmployees, label: displayName }));
